import * as React from 'react';
import { Suspense, useCallback, useEffect, useRef, useState } from 'react';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection';
import { mergeRegister } from '@lexical/utils';
import {
  $createParagraphNode,
  $getNodeByKey,
  $getSelection,
  $isNodeSelection,
  $isRangeSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_LOW,
  createCommand,
  DRAGSTART_COMMAND,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  KEY_ENTER_COMMAND,
  KEY_ESCAPE_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';

import { useFileUpload } from '../hooks/useFileUpload';
import ImageResizer from '../ui/ImageResizer';
import { imageUploadHandler } from '../utils/imageUploadHandler';
import { $isImageNode } from './ImageNode';
import Spinner from '@/dashboard/components/Spinner';

export const RIGHT_CLICK_IMAGE_COMMAND = createCommand('RIGHT_CLICK_IMAGE_COMMAND');

function LazyImage({
  altText,
  className,
  imageRef = { current: null },
  src,
  width = 'inherit',
  height = 'inherit',
  maxWidth,
  id,
}) {
  const [currentSrc, setCurrentSrc] = useState(src);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (src !== currentSrc) {
      setIsLoading(true);
      const img = new Image();
      img.src = src;
      img.onload = () => {
        setCurrentSrc(src);
        setIsLoading(false);
      };
    }
  }, [src]);

  return (
    <>
      <img
        className={className || undefined}
        src={currentSrc}
        alt={altText}
        ref={imageRef}
        style={{
          height,
          maxWidth,
          width,
        }}
        draggable="false"
        id={id}
      />
      {isLoading && (
        <div className="absolute top-0 right-0">
          <Spinner />
        </div>
      )}
    </>
  );
}

export default function ImageComponent({
  src,
  altText,
  nodeKey,
  width = 'inherit',
  height = 'inherit',
  maxWidth,
  resizable,
  isLoading,
  id,
}) {
  const imageRef = useRef(null);
  const buttonRef = useRef(null);
  const [isSelected, setSelected, clearSelection] = useLexicalNodeSelection(nodeKey);
  const [isResizing, setIsResizing] = useState(false);
  const [editor] = useLexicalComposerContext();
  const [selection, setSelection] = useState(null);
  const activeEditorRef = useRef(null);
  const imageUploader = useFileUpload('image');

  const onDelete = useCallback(
    payload => {
      if (isSelected && $isNodeSelection($getSelection())) {
        editor.update(() => {
          const event = payload;
          event.preventDefault();

          const node = $getNodeByKey(nodeKey);

          if ($isImageNode(node)) {
            // insert paragraph
            const paragraph = $createParagraphNode();
            node.insertAfter(paragraph);

            // remove image
            node.remove();

            // select paragraph
            paragraph.select();
          }
        });

        return true;
      }
      return false;
    },
    [isSelected, nodeKey],
  );

  const onEnter = useCallback(
    event => {
      const latestSelection = $getSelection();
      const buttonElem = buttonRef.current;
      if (
        isSelected &&
        $isNodeSelection(latestSelection) &&
        latestSelection.getNodes().length === 1
      ) {
        if (buttonElem !== null && buttonElem !== document.activeElement) {
          event.preventDefault();
          buttonElem.focus();
          return true;
        }
      }
      return false;
    },
    [isSelected],
  );

  const onEscape = useCallback(
    event => {
      if (buttonRef.current === event.target) {
        editor.update(() => {
          setSelected(true);
          const parentRootElement = editor.getRootElement();
          if (parentRootElement !== null) {
            parentRootElement.focus();
          }
        });
        return true;
      }
      return false;
    },
    [editor, setSelected],
  );

  const onClick = useCallback(
    payload => {
      const event = payload;

      if (isResizing) {
        return true;
      }
      if (event.target === imageRef.current) {
        if (event.shiftKey) {
          setSelected(!isSelected);
        } else {
          clearSelection();
          setSelected(true);
        }
        return true;
      }

      return false;
    },
    [isResizing, isSelected, setSelected, clearSelection],
  );

  const onRightClick = useCallback(
    event => {
      editor.getEditorState().read(() => {
        const latestSelection = $getSelection();
        const domElement = event.target;
        if (
          domElement.tagName === 'IMG' &&
          $isRangeSelection(latestSelection) &&
          latestSelection.getNodes().length === 1
        ) {
          editor.dispatchCommand(RIGHT_CLICK_IMAGE_COMMAND, event);
        }
      });
    },
    [editor],
  );

  useEffect(() => {
    let isMounted = true;
    const rootElement = editor.getRootElement();
    const unregister = mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        if (isMounted) {
          setSelection(editorState.read(() => $getSelection()));
        }
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_, activeEditor) => {
          activeEditorRef.current = activeEditor;
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(CLICK_COMMAND, onClick, COMMAND_PRIORITY_LOW),
      editor.registerCommand(RIGHT_CLICK_IMAGE_COMMAND, onClick, COMMAND_PRIORITY_LOW),
      editor.registerCommand(
        DRAGSTART_COMMAND,
        event => {
          if (event.target === imageRef.current) {
            // TODO This is just a temporary workaround for FF to behave like other browsers.
            // Ideally, this handles drag & drop too (and all browsers).
            event.preventDefault();
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(KEY_DELETE_COMMAND, onDelete, COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_BACKSPACE_COMMAND, onDelete, COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_ENTER_COMMAND, onEnter, COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_ESCAPE_COMMAND, onEscape, COMMAND_PRIORITY_LOW),
    );

    rootElement?.addEventListener('contextmenu', onRightClick);

    return () => {
      isMounted = false;
      unregister();
      rootElement?.removeEventListener('contextmenu', onRightClick);
    };
  }, [
    clearSelection,
    editor,
    isResizing,
    isSelected,
    nodeKey,
    onDelete,
    onEnter,
    onEscape,
    onClick,
    onRightClick,
    setSelected,
  ]);

  const onResizeEnd = (nextWidth = 'inherit', nextHeight = 'inherit') => {
    // Delay hiding the resize bars for click case
    setTimeout(() => {
      setIsResizing(false);
    }, 200);

    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      if ($isImageNode(node)) {
        node.setWidthAndHeight(nextWidth, nextHeight);
      }
    });
  };

  const onResizeStart = () => {
    setIsResizing(true);
  };

  const draggable = isSelected && $isNodeSelection(selection) && !isResizing;
  const isFocused = isSelected || isResizing;

  const onFileChange = async e => {
    const files = e.target.files;

    // reset original src so it can be replaced with preview and upload progress
    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      node.src = '';
    });

    return await imageUploadHandler(files, nodeKey, editor, imageUploader.upload);
  };

  if (src === null) {
    return <EmptyImageComponent errors={imageUploader.errors} onFileChange={onFileChange} />;
  }

  return (
    <Suspense fallback={null}>
      <>
        <div draggable={draggable} className="relative inline-block max-w-full">
          <LazyImage
            className={
              isFocused ? `focused ${$isNodeSelection(selection) ? 'draggable' : ''}` : null
            }
            src={src}
            altText={altText}
            imageRef={imageRef}
            width={width}
            height={height}
            maxWidth={maxWidth}
            id={id}
          />
          {isLoading && (
            <span className="absolute top-0 right-0">
              <Spinner />
            </span>
          )}

          {resizable && $isNodeSelection(selection) && isFocused && (
            <ImageResizer
              editor={editor}
              buttonRef={buttonRef}
              imageRef={imageRef}
              maxWidth={maxWidth}
              onResizeStart={onResizeStart}
              onResizeEnd={onResizeEnd}
            />
          )}
        </div>
      </>
    </Suspense>
  );
}

function EmptyImageComponent({ onChange }) {
  return (
    <div className="">
      Upload an image
      <div>
        <input type="file" name="image-input" onChange={onChange} />
      </div>
    </div>
  );
}
